import { useEffect, useState } from 'react'
import { User, Settings as SettingsIcon, Bell } from 'lucide-react'
import { advancedApi, type UsageResponse } from '../api'
import { useAuthStore } from '../store'
import { useToastStore } from '../store/toast'
import UpgradeModal from '../components/UpgradeModal'
import UsageMeter from '../components/UsageMeter'

export default function SettingsPage() {
  const user = useAuthStore((state) => state.user)
  const addToast = useToastStore((state) => state.addToast)
  const [loading, setLoading] = useState(false)
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  useEffect(() => {
    advancedApi.getUsage().then((res) => setUsage(res.data)).catch(() => setUsage(null))
  }, [])

  const handleSave = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      addToast({
        type: 'success',
        message: 'Settings saved successfully!',
      })
    }, 1000)
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account preferences</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3 space-x-reverse">
            <User className="w-6 h-6 text-brand-blue" />
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>
        </div>
      </div>

      <div id="subscription" className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Subscription and usage</h2>
            <p className="text-sm text-gray-500 mt-1">
              Free accounts can test 3 PDFs per month, process 1 page per document, and create 1 export.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setUpgradeOpen(true)}
            className="inline-flex items-center justify-center rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-accent"
          >
            Upgrade plan
          </button>
        </div>
        <div className="p-6">
          <UsageMeter />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3 space-x-reverse">
            <SettingsIcon className="w-6 h-6 text-brand-blue" />
            <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Language</h3>
              <p className="text-sm text-gray-500">Select your preferred language</p>
            </div>
            <select className="px-4 py-2 border border-gray-300 rounded-lg">
              <option value="en">English</option>
              <option value="ar">العربية</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3 space-x-reverse">
            <Bell className="w-6 h-6 text-brand-blue" />
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-500">Receive updates via email</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-brand-blue rounded" defaultChecked />
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-8 py-3 bg-brand-blue text-white font-semibold rounded-lg hover:bg-brand-accent transition-colors disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlan={usage?.plan_type ?? 'free'}
      />
    </div>
  )
}
